// Helpers compartidos entre queries.
//
// useApiQuery / useApiMutation: envuelven useQuery / useMutation de
// TanStack Query con el apiFetch de la app. La diferencia con usar
// useQuery directo es que el apiFetch ya inyecta Authorization y
// maneja 401→refresh, así que no tenemos que duplicar eso en cada
// query.
//
// Uso:
//   const { data, isLoading } = useApiQuery(['camadas'], () =>
//     apiFetch<Camada>('/v1/camadas/...'),
//   );

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { cacheReadThrough, canUseOfflineCache } from '@/lib/offline/cache';
import { useEffect, useRef, useState } from 'react';
import { currentBundle } from '@/lib/offline/bundle';
import type { OfflineBundle } from '@/lib/offline/domainTypes';

// useQuery tipado: queryFn siempre presente. El caller no necesita
// pasar queryKey/queryFn fuera del object literal porque ya los
// bindeamos en el primer argumento.
export function useApiQuery<TData>(options: {
  queryKey: readonly unknown[];
  queryFn: () => Promise<TData>;
  enabled?: boolean;
  staleTime?: number;
  refetchInterval?: number;
  project?: (data: TData) => Promise<TData>;
}): UseQueryResult<TData> {
  const { queryKey, queryFn, enabled, staleTime, refetchInterval } = options;
  const ownerId = useAuthStore((s) => s.user?.id ?? null);
  const load = canUseOfflineCache(queryKey) ? () => cacheReadThrough(queryKey, queryFn) : queryFn;
  return useQuery<TData>({
    queryKey: [...queryKey, 'owner', ownerId],
    queryFn: async () => {
      const data = await load();
      return options.project ? options.project(data) : data;
    },
    networkMode: 'always',
    enabled,
    staleTime,
    refetchInterval,
  });
}

export function useApiMutation<TInput, TOutput>(options: {
  mutationFn: (input: TInput, context: { offlineBase: OfflineBundle | null }) => Promise<TOutput>;
  // Query keys a invalidar tras el éxito de la mutación. Cada entry
  // es un queryKey completo (lo que devuelve `qk(...)`).
  invalidateKeys?: ReadonlyArray<readonly unknown[]>;
}): UseMutationResult<TOutput, Error, TInput> {
  const qc = useQueryClient();
  const ownerId = useAuthStore((state) => state.user?.id ?? null);
  const [, refresh] = useState(0);
  useEffect(() => {
    const changed = () => refresh((value) => value + 1);
    window.addEventListener('offline:change', changed);
    return () => window.removeEventListener('offline:change', changed);
  }, []);
  const viewed = useRef({ ownerId, base: currentBundle() });
  if (viewed.current.ownerId !== ownerId) viewed.current = { ownerId, base: currentBundle() };
  else if (!viewed.current.base) viewed.current.base = currentBundle();
  return useMutation<TOutput, Error, TInput>({
    networkMode: 'always',
    mutationFn: (input) => options.mutationFn(input, { offlineBase: viewed.current.base }),
    onSuccess: async () => {
      viewed.current.base = currentBundle();
      await Promise.all(
        (options.invalidateKeys ?? []).map((key) => qc.invalidateQueries({ queryKey: key })),
      );
    },
  });
}

// Helper para los queryKey tipados.
export function qk(
  ...parts: ReadonlyArray<string | number | boolean | null | undefined>
): readonly unknown[] {
  return parts;
}

export { apiFetch };
